import {rootState} from '@mintdata-wsp/common/state/RootState';
import {Workbook} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/Workbook';
import {PropertiesVisiblePopover} from '@mintdata-wsp/common/state/ui/visualDesigner/SurfaceEditorState';
import {controllers} from '@mintdata-wsp/wsp/controllers/WspControllers';
import {DragGhostDirection, ResizeEvent, ResizePhase, Resizer} from '@mintdata-wsp/wsp/ui/components/resizable/Resizer';
import {CommandMenu} from '@mintdata-wsp/wsp/ui/views/visualDesigner/commandMenu/CommandMenu';
import {PropertiesContainer} from '@mintdata-wsp/wsp/ui/views/visualDesigner/container/PropertiesContainer';
import {SurfaceContainer} from '@mintdata-wsp/wsp/ui/views/visualDesigner/container/SurfaceContainer';
import {SpreadsheetView} from '@mintdata-wsp/wsp/ui/views/visualDesigner/spreadsheet/SpreadsheetView';
import {SurfaceArea} from '@mintdata-wsp/wsp/ui/views/visualDesigner/surface/area/SurfaceArea';
import {GuidePanel} from '@mintdata-wsp/wsp/ui/views/visualDesigner/surface/panels/guide/GuidePanel';
import {GuideTour} from '@mintdata-wsp/wsp/ui/views/visualDesigner/surface/panels/guide/GuideTour';
import {GuideVideo} from '@mintdata-wsp/wsp/ui/views/visualDesigner/surface/panels/guide/GuideVideo';
import {SurfaceOutlinePanel} from '@mintdata-wsp/wsp/ui/views/visualDesigner/surface/panels/outline/SurfaceOutlinePanel';
import {SurfacePropertiesPanel} from '@mintdata-wsp/wsp/ui/views/visualDesigner/surface/panels/properties/SurfacePropertiesPanel';
import {SurfaceDnDGlass} from '@mintdata-wsp/wsp/ui/views/visualDesigner/surface/SurfaceDnDGlass';
import {SurfaceEditorGlass} from '@mintdata-wsp/wsp/ui/views/visualDesigner/surface/SurfaceEditorGlass';
import {TopBar} from '@mintdata-wsp/wsp/ui/views/visualDesigner/topBar/TopBar';
import * as classnames from 'classnames/bind';
import {action, observable} from 'mobx';
import {observer} from 'mobx-react';
import * as React from 'react';

const styles = require('./VisualDesignerBody.less');
const cx = classnames.bind(styles);

const SPREADSHEET_MIN_WIDTH_IN_PX = 48;
const LEFT_PANEL_MIN_WIDTH_IN_PX = 250;

export interface IVisualDesignerBodyState
{
    leftColResizerInProgressOffset:number;
    rightColResizerInProgressOffset:number;
}

@observer
export class VisualDesignerBody extends React.Component<any, any>
{
    private _gridRightColumn?:HTMLElement | null;

    private _refHandlers = {
        grid: (ref:HTMLElement | null) => controllers.ui.workbenchVisibility.setGridDomRef(ref),
        gridLeftPanels: (ref:HTMLElement | null) => controllers.ui.workbenchVisibility.setGridLeftPanelsDomRef(ref),
        gridGlass: (ref:HTMLElement | null) => controllers.ui.workbenchVisibility.setGridGlassDomRef(ref),
        gridRightPanels: (ref:HTMLElement | null) => controllers.ui.workbenchVisibility.setGridRightPanelsDomRef(ref),
        gridRightColumn: (ref:HTMLElement | null) => this._gridRightColumn = ref
    };

    @observable
    private _state:IVisualDesignerBodyState = {
        leftColResizerInProgressOffset: 0,
        rightColResizerInProgressOffset: 0
    };

    private _getRootWorkbook():Workbook
    {
        const openDesign = controllers.ui.openDesign.getOpenDesign();

        if( !openDesign || !openDesign.designState || !openDesign.designState.spreadsheets )
            return null;

        return openDesign.designState.spreadsheets.rootWorkbook;
    }

    private _getMaxRightColumnResizerInProgressOffset():number
    {
        const {workbenchVisibility} = controllers.ui;
        const {rightColResizerInProgressOffset} = this._state;

        if( !this._gridRightColumn )
        {
            return 0;
        }

        if( rightColResizerInProgressOffset >= 0 )
        {
            return rightColResizerInProgressOffset;
        }

        const maxRightColumnWidth = workbenchVisibility.getMaxRightColumnWidth();
        const rightColumnWidthInPercent = workbenchVisibility.getGridRightColumnWidth();
        const rightColumnWidthPx = this._gridRightColumn.getBoundingClientRect().width;
        const onePercentToPx = rightColumnWidthPx / rightColumnWidthInPercent;
        const maxResizerInProgressOffset = rightColumnWidthPx - (maxRightColumnWidth * onePercentToPx);

        return Math.max(maxResizerInProgressOffset, rightColResizerInProgressOffset);
    }

    @action
    private _handleRightColumnResize = (event:ResizeEvent) =>
    {
        const {workbenchVisibility, surfacePropertiesPanel} = controllers.ui;

        this._state.rightColResizerInProgressOffset += event.deltaX;

        if( event.phase === ResizePhase.Start )
        {
            surfacePropertiesPanel.setPropertiesPopover(PropertiesVisiblePopover.None);
        }
        else if( event.phase === ResizePhase.Stop )
        {
            const rightColumnWidthInPercent = workbenchVisibility.getGridRightColumnWidth();
            const rightColumnWidthPx = this._gridRightColumn.getBoundingClientRect().width;
            const onePercentToPx = rightColumnWidthPx / rightColumnWidthInPercent;
            const maxResizerInProgressOffset = this._getMaxRightColumnResizerInProgressOffset();
            const newWidthInPx = rightColumnWidthPx - maxResizerInProgressOffset;
            const newWidthInPercent = newWidthInPx / onePercentToPx;

            if( newWidthInPx < SPREADSHEET_MIN_WIDTH_IN_PX )
            {
                workbenchVisibility.setSpreadsheetVisibility(false);
            }
            else
            {
                workbenchVisibility.setGridRightColumnWidth(newWidthInPercent);
            }

            this._state.rightColResizerInProgressOffset = 0;
        }
    };

    private _getMaxLeftColumnResizerInProgressOffset():number
    {
        const {workbenchVisibility} = controllers.ui;
        const {leftColResizerInProgressOffset} = this._state;
        const leftColumnWidthPx:number = workbenchVisibility.getGridLeftColumnWidthPx();

        if( leftColResizerInProgressOffset > 0 )
            return Math.min(leftColResizerInProgressOffset, leftColumnWidthPx - LEFT_PANEL_MIN_WIDTH_IN_PX);

        const maxLeftColumnWidthPx:number = workbenchVisibility.getMaxLeftColumnWidthPx();
        return Math.max(leftColResizerInProgressOffset, -(maxLeftColumnWidthPx - leftColumnWidthPx));
    }

    @action
    private _handleLeftColumnResize = (event:ResizeEvent) =>
    {
        const {workbenchVisibility} = controllers.ui;

        this._state.leftColResizerInProgressOffset -= event.deltaX;

        if( event.phase === ResizePhase.Stop )
        {
            const leftColumnWidthPx:number = workbenchVisibility.getGridLeftColumnWidthPx();
            const maxResizerInProgressOffset:number = this._getMaxLeftColumnResizerInProgressOffset();
            const newWidthInPx:number = leftColumnWidthPx - maxResizerInProgressOffset;

            if( newWidthInPx <= LEFT_PANEL_MIN_WIDTH_IN_PX )
                workbenchVisibility.setGridLeftColumnWidthPx(LEFT_PANEL_MIN_WIDTH_IN_PX);
            else
                workbenchVisibility.setGridLeftColumnWidthPx(newWidthInPx);

            this._state.leftColResizerInProgressOffset = 0;
        }
    };

    private _renderGridLeftColumn():JSX.Element
    {
        const {workbenchVisibility, surfaceFileUpload} = controllers.ui;

        const isSpreadsheetVisible = workbenchVisibility.isSpreadsheetVisible();
        const isSpreadsheetFullScreen = workbenchVisibility.isSpreadsheetFullScreen();
        const isPagesPanelVisible = workbenchVisibility.isPagesPanelVisible();

        if( isSpreadsheetVisible && isSpreadsheetFullScreen )
            return null;

        const isLeftColResizerInProgress:boolean = this._state.leftColResizerInProgressOffset !== 0;
        const isFileDraggingOverSurface:boolean = surfaceFileUpload.getIsFileDraggingOverSurface();

        return (
            <div className={cx('gridLeftColumn')}>
                <TopBar />
                <div className={cx('gridPanels')}>
                    <div
                        ref={this._refHandlers.gridLeftPanels}
                        className={cx('gridLeftPanels')}
                    >
                        {isPagesPanelVisible && <div
                            className={cx('gridPanel', {noPointerEvents: isLeftColResizerInProgress})}
                            style={{
                                width: `${workbenchVisibility.getGridLeftColumnWidthPx()}px`
                            }}
                        >
                            <SurfaceOutlinePanel className={cx('surfaceOutlinePanel')} />
                            <Resizer
                                isVisible={true}
                                isVertical={true}
                                className={cx('gridLeftColumnResizer')}
                                dragGhostDirection={DragGhostDirection.Right}
                                dragGhostOffset={this._getMaxLeftColumnResizerInProgressOffset()}
                                onResize={this._handleLeftColumnResize}
                            />
                        </div>}
                    </div>
                    <div
                        ref={this._refHandlers.gridGlass}
                        className={cx('gridGlass')}
                    >
                        <div className={cx('dropzone', {isFileDraggingOverSurface})} />
                    </div>
                    <div
                        ref={this._refHandlers.gridRightPanels}
                        className={cx('gridRightPanels')}
                    >
                        <PropertiesContainer className={cx('gridPanel')}>
                            <SurfacePropertiesPanel />
                        </PropertiesContainer>
                    </div>

                    <SurfaceEditorGlass />
                    <SurfaceDnDGlass />

                    <GuideTour />
                    <GuidePanel />
                    <GuideVideo />
                </div>
            </div>
        );
    }

    private _renderGridRightColumn():JSX.Element
    {
        const {workbenchVisibility} = controllers.ui;

        const rootWorkbook = this._getRootWorkbook();
        const isVisible = workbenchVisibility.isSpreadsheetVisible();
        const isSpreadsheetFullScreen = workbenchVisibility.isSpreadsheetFullScreen();

        if( !isVisible )
            return null;

        const maxResizerInProgressOffset = this._getMaxRightColumnResizerInProgressOffset();
        const flexBasis = isSpreadsheetFullScreen ? 100 : workbenchVisibility.getGridRightColumnWidth();
        const isLeftColResizerInProgress:boolean = this._state.leftColResizerInProgressOffset !== 0;

        const style:React.CSSProperties = {
            flexBasis: `${flexBasis}%`
        };

        return (
            <div
                ref={this._refHandlers.gridRightColumn}
                className={cx('gridRightColumn', {noPointerEvents: isLeftColResizerInProgress})}
                style={style}
            >
                {
                    !isSpreadsheetFullScreen &&
                    <Resizer
                        isVisible={true}
                        isVertical={true}
                        className={cx('gridRightColumnResizer')}
                        dragGhostDirection={DragGhostDirection.Left}
                        dragGhostOffset={maxResizerInProgressOffset}
                        onResize={this._handleRightColumnResize}
                    />
                }
                <SpreadsheetView workbookId={rootWorkbook.id} />
            </div>
        );
    }

    render():JSX.Element
    {
        const {rightColResizerInProgressOffset} = this._state;
        const {ui: {visualDesigner: {sampleDesign}}} = rootState;
        const isShowOnlyTable = sampleDesign && !sampleDesign.showSurface;

        return (
            <div className={cx('base', 'noOutline', {
                disableTextSelection: !!rightColResizerInProgressOffset,
                isZenPreviewMode: controllers.ui.surfaceArea.isInZenPreviewMode()
            })}>

                {
                    !isShowOnlyTable &&
                    <TopBar isGhost={true} />
                }

                {
                    !isShowOnlyTable &&
                    <SurfaceContainer>
                        <SurfaceArea />
                    </SurfaceContainer>
                }

                <div
                    ref={this._refHandlers.grid}
                    className={cx('grid')}
                >
                    {!isShowOnlyTable && this._renderGridLeftColumn()}
                    {this._renderGridRightColumn()}
                    <CommandMenu />
                </div>
            </div>
        );
    }
}
